import moment from 'moment';
import { getPercent } from '../../helpers/showDelta';

export const PERIOD_DAYS = 30;

/**
 * The two 30-day windows the report compares, covering the last 60 days ending yesterday.
 *
 * Today is never queried: Search Console is still collecting it, so it comes back partial or —
 * with the usual reporting lag — missing altogether, which both deflated the current window and
 * ended every sparkline in a dive toward zero.
 *
 * `dataState: 'ALL'` means the days just before it can still be partial. Shift `endDate` back
 * further if the comparison should only ever use finalized days.
 */
export function getPeriods() {
    const endDate = moment().startOf('day').subtract(1, 'days');
    const currentStart = endDate.clone().subtract(PERIOD_DAYS - 1, 'days');
    const priorEnd = currentStart.clone().subtract(1, 'days');
    const priorStart = priorEnd.clone().subtract(PERIOD_DAYS - 1, 'days');

    return {
        startDate: priorStart.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        currentStart: currentStart.format('YYYY-MM-DD'),
        currentEnd: endDate.format('YYYY-MM-DD'),
        priorStart: priorStart.format('YYYY-MM-DD'),
        priorEnd: priorEnd.format('YYYY-MM-DD'),
    };
}

/** Every calendar date from `start` to `end` inclusive, as YYYY-MM-DD strings. */
function dateAxis(start, end) {
    const dates = [];
    const cursor = moment(start);
    const last = moment(end);
    while (cursor.isSameOrBefore(last)) {
        dates.push(cursor.format('YYYY-MM-DD'));
        cursor.add(1, 'days');
    }
    return dates;
}

function sum(values) {
    return values.reduce((total, value) => total + value, 0);
}

/**
 * Turns the daily GSC rows for one property into the three metric cells the table renders.
 *
 * Each metric carries the current 30-day figure, the same figure for the prior 30 days (so the
 * cell can show the change), and a per-day series for the sparkline. The series spans both
 * windows — all 60 days — so the line a cell draws next to a change of -20% actually contains
 * the days that change is measured against. The series is built against a full calendar axis
 * rather than the returned rows, because GSC omits days with no data entirely — without the axis
 * a quiet week would compress the line instead of flattening it.
 *
 * CTR is deliberately recomputed as period clicks / period impressions rather than averaged from
 * the daily ratios: averaging would weight a day with 3 impressions the same as a day with
 * 300,000.
 */
export function summarizeDomainMetrics(rows, periods) {
    const byDate = {};
    for (const row of rows ?? []) {
        byDate[row.date] = row;
    }

    const readWindow = (start, end) => {
        const dates = dateAxis(start, end);
        const clicks = dates.map((date) => byDate[date]?.clicks ?? 0);
        const impressions = dates.map((date) => byDate[date]?.impressions ?? 0);
        return { dates, clicks, impressions };
    };

    const current = readWindow(periods.currentStart, periods.currentEnd);
    const prior = readWindow(periods.priorStart, periods.priorEnd);
    const trend = readWindow(periods.startDate, periods.endDate);

    const currentClicks = sum(current.clicks);
    const currentImpressions = sum(current.impressions);
    const priorClicks = sum(prior.clicks);
    const priorImpressions = sum(prior.impressions);

    const ratio = (clicks, impressions) => (impressions ? clicks / impressions : 0);

    return {
        dates: trend.dates,
        clicks: {
            value: currentClicks,
            prior: priorClicks,
            series: trend.clicks,
        },
        impressions: {
            value: currentImpressions,
            prior: priorImpressions,
            series: trend.impressions,
        },
        ctr: {
            value: ratio(currentClicks, currentImpressions),
            prior: ratio(priorClicks, priorImpressions),
            series: trend.clicks.map((clicks, i) => ratio(clicks, trend.impressions[i])),
        },
    };
}

/**
 * The change from the prior period to the current one, as a percentage, or null where no
 * percentage is meaningful: a metric with nothing in either period. `getPercent` covers the
 * remaining edge — growth from zero, which it reports as Infinity — and callers render that as
 * the infinity sign rather than a number.
 */
export function deltaPercent(metric) {
    if (!metric) return null;
    if (!metric.value && !metric.prior) return null;
    return getPercent(metric.value, metric.prior);
}
